import { useEffect, useState } from "react";
import { UserPlus, Mail, ShieldCheck, Copy, Send, ShieldOff, Smartphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ROLES = ["admin", "coordinator", "manager"];
const STATUS_STYLE = {
  active: "text-emerald-300 border-emerald-500/40",
  invited: "text-cyan-300 border-cyan-500/40",
  deactivated: "text-red-300 border-red-500/40",
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("manager");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [stats, setStats] = useState(null);
  const [purging, setPurging] = useState(false);

  const loadStats = () => api.get("/admin/data-stats").then((r) => setStats(r.data)).catch(() => {});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data);
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const purgeDemo = async () => {
    if (!window.confirm("Remove ALL demo/seed referrals (created_by = 'seed') and their logs? Real referrals entered by staff are NOT affected.")) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge-demo");
      toast.success(`Purged ${data.referrals_deleted} demo referral(s)`);
      loadStats();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Purge failed"); }
    finally { setPurging(false); }
  };

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/users/invite", { name, email: email.trim().toLowerCase(), role });
      toast.success(data.email_sent ? "Invite email sent" : "User created — email not sent, copy the invite link");
      if (!data.email_sent) navigator.clipboard?.writeText(data.invite_link).catch(() => {});
      setName(""); setEmail(""); setRole("manager");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invite failed");
    } finally { setBusy(false); }
  };

  const changeRole = async (u, newRole) => {
    try {
      await api.put(`/auth/users/${u.id}/role`, { role: newRole });
      toast.success(`${u.email} is now ${newRole}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/auth/users/${u.id}/active`, { active: u.active === false });
      toast.success(u.active === false ? "User reactivated" : "User deactivated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const resend = async (u) => {
    try {
      const { data } = await api.post(`/auth/users/${u.id}/resend-invite`);
      toast.success(data.email_sent ? "Invite re-sent" : "Copied invite link (email not sent)");
      if (!data.email_sent) navigator.clipboard?.writeText(data.invite_link).catch(() => {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const resetMfa = async (u) => {
    if (!window.confirm(`Reset two-factor for ${u.email}? They'll be signed out and must set up 2FA again on next login.`)) return;
    try {
      await api.post(`/auth/users/${u.id}/reset-mfa`);
      toast.success(`Two-factor reset for ${u.email}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/auth/test-email");
      toast[data.ok ? "success" : "error"](data.ok ? `Test email sent to ${data.to}` : "Email not configured / send failed");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setTesting(false); }
  };

  return (
    <div className="p-6 space-y-5 max-w-5xl" data-testid="users-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head font-black text-3xl tracking-tight">User Management</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">Invite team members and set their access level. Self sign-up is disabled.</p>
        </div>
        <Button onClick={sendTest} disabled={testing} data-testid="send-test-email-btn"
                className="gap-2 bg-violet-500 hover:bg-violet-400 text-slate-950 font-semibold">
          <Send className="w-4 h-4" /> {testing ? "Sending…" : "Send test email"}
        </Button>
      </div>

      <form onSubmit={invite} className="glass glass-cyan rounded-xl p-4" data-testid="invite-form">
        <div className="flex items-center gap-2 mb-3"><UserPlus className="w-4 h-4 text-cyan-400" />
          <h2 className="font-head font-bold text-sm uppercase tracking-wide">Invite a user</h2></div>
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div className="space-y-1"><Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" className="bg-slate-900/60 border-white/10" required /></div>
          <div className="space-y-1"><Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" className="bg-slate-900/60 border-white/10" required /></div>
          <div className="space-y-1"><Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Role</Label>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role"
                    className="w-full h-10 rounded-md bg-slate-900/60 border border-white/10 px-3 text-sm">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select></div>
          <Button type="submit" disabled={busy} data-testid="invite-submit" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold gap-2">
            <Mail className="w-4 h-4" /> {busy ? "Sending…" : "Send invite"}
          </Button>
        </div>
      </form>

      <div className="glass rounded-xl p-4" data-testid="users-list">
        <h2 className="font-head font-bold text-sm uppercase tracking-wide text-muted-foreground mb-3">Team ({users.length})</h2>
        {loading ? (
          <p className="text-xs text-muted-foreground font-mono">Loading…</p>
        ) : (
          <div className="space-y-1.5">
            {users.map((u) => {
              const isSelf = u.id === user?.id;
              return (
                <div key={u.id} data-testid={`user-row-${u.id}`}
                     className="flex items-center justify-between gap-3 flex-wrap rounded-md border border-white/8 px-3 py-2">
                  <div className="min-w-[180px]">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      {u.name || "—"} {isSelf && <span className="text-[10px] font-mono text-cyan-400 border border-cyan-500/40 rounded px-1">YOU</span>}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono truncate">{u.email}</div>
                  </div>
                  <span className={`text-[11px] font-mono border rounded px-2 py-0.5 uppercase ${STATUS_STYLE[u.status] || ""}`} data-testid={`user-status-${u.id}`}>{u.status}</span>
                  <span data-testid={`user-mfa-${u.id}`}
                        className={`text-[11px] font-mono border rounded px-2 py-0.5 uppercase inline-flex items-center gap-1 ${u.mfa_enabled ? "text-emerald-300 border-emerald-500/40" : "text-amber-300 border-amber-500/40"}`}>
                    {u.mfa_enabled ? <Smartphone className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
                    {u.mfa_enabled ? `2FA · ${u.recovery_remaining ?? 0} codes` : "No 2FA"}
                  </span>
                  {u.is_super_admin && (
                    <span data-testid={`user-super-${u.id}`}
                          className="text-[11px] font-mono border rounded px-2 py-0.5 uppercase text-violet-300 border-violet-500/40">Super Admin</span>
                  )}
                  <select value={u.role} disabled={isSelf || u.is_super_admin} onChange={(e) => changeRole(u, e.target.value)}
                          data-testid={`user-role-${u.id}`}
                          className="h-8 rounded-md bg-slate-900/60 border border-white/10 px-2 text-xs disabled:opacity-40">
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <div className="flex items-center gap-2">
                    {u.status === "invited" && (
                      <button onClick={() => resend(u)} data-testid={`resend-${u.id}`}
                              className="text-xs font-mono text-cyan-300 hover:text-cyan-200 flex items-center gap-1">
                        <Copy className="w-3.5 h-3.5" /> Resend
                      </button>
                    )}
                    {u.mfa_enabled && (
                      <button onClick={() => resetMfa(u)} data-testid={`reset-mfa-${u.id}`}
                              className="text-xs font-mono text-amber-300 hover:text-amber-200 flex items-center gap-1">
                        <ShieldOff className="w-3.5 h-3.5" /> Reset 2FA
                      </button>
                    )}
                    <button onClick={() => toggleActive(u)} disabled={isSelf || u.is_super_admin} data-testid={`toggle-active-${u.id}`}
                            className={`text-xs font-mono flex items-center gap-1 disabled:opacity-30 ${u.active === false ? "text-emerald-300 hover:text-emerald-200" : "text-red-300 hover:text-red-200"}`}>
                      <ShieldCheck className="w-3.5 h-3.5" /> {u.active === false ? "Reactivate" : "Deactivate"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="glass rounded-xl p-4" data-testid="data-maintenance">
        <h2 className="font-head font-bold text-sm uppercase tracking-wide text-muted-foreground mb-3">Data maintenance</h2>
        {stats ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs font-mono text-muted-foreground space-y-0.5">
              <div data-testid="stat-total">Referrals total: <span className="text-foreground">{stats.referrals_total}</span></div>
              <div data-testid="stat-demo">Demo / seed referrals: <span className={stats.referrals_demo ? "text-amber-300" : "text-foreground"}>{stats.referrals_demo}</span></div>
              <div>Real referrals: <span className="text-foreground">{stats.referrals_real}</span> · Contacts: {stats.contacts} · Users: {stats.users}</div>
            </div>
            <Button onClick={purgeDemo} disabled={purging || !stats.referrals_demo} data-testid="purge-demo-btn"
                    className="gap-2 bg-red-500/90 hover:bg-red-500 text-white font-semibold disabled:opacity-40">
              <Trash2 className="w-4 h-4" /> {purging ? "Purging…" : `Purge demo data${stats.referrals_demo ? ` (${stats.referrals_demo})` : ""}`}
            </Button>
          </div>
        ) : <p className="text-xs text-muted-foreground font-mono">Loading…</p>}
      </div>
    </div>
  );
}
